import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider as SuperApolloProvider,
  split,
  HttpLink,
  from,
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { getBackendDetails } from '@/utils/getBackendDetails';
import { apolloTypePolicies } from './apolloCache';

const { hostname, httpProtocol, wsProtocol } = getBackendDetails();
const httpUri = `${httpProtocol}://${hostname}/graphql`;
const wsUri = `${wsProtocol}://${hostname}/graphql`;

// Configure RetryLink with delay and retry attempt settings
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 10_000,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error) => !!error,
  },
});

// Auth link to add JWT token to requests
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('accessToken');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// Error link to handle token refresh
const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (err.extensions?.code === 'UNAUTHENTICATED') {
          // Token expired, try to refresh
          return forward(operation);
        }
      }
    }

    if (networkError) {
      console.log(`[Network error]: ${networkError}`);
    }
  },
);

// HTTP Link for queries and mutations
const httpLink = new HttpLink({
  uri: httpUri,
  credentials: 'include', // Include credentials like cookies (JWT)
});

// Combined links
const combinedHttpLink = from([errorLink, authLink, retryLink, httpLink]);

// WebSocket Link for subscriptions
const wsLink = new GraphQLWsLink(
  createClient({
    url: wsUri,
    retryAttempts: 1000, // Maximum number of retry attempts
    shouldRetry: () => true, // Always attempt to retry the connection
    connectionParams: () => {
      const token = localStorage.getItem('accessToken');
      return {
        authorization: token ? `Bearer ${token}` : '',
      };
    },
  }),
);

// Split the links based on operation type
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink, // Use wsLink for subscriptions
  combinedHttpLink, // Use httpLink for queries and mutations
);

// Create the Apollo Client instance
const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: apolloTypePolicies,
  }),
});

export function ApolloProvider({ children }: { children: React.ReactNode }) {
  return <SuperApolloProvider client={client}>{children}</SuperApolloProvider>;
}
